import PropTypes from "prop-types";
import React from "react";

const fmtCurrency = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

/* ponytail: data-viz bar palette, cycles per ticket type. */
const BAR_COLORS = ["#1982C4", "#5f5383", "#FF7A00", "#98c648", "#FF595E"];
const PRESALE_COLOR = "#1982C4";
const PUBLIC_COLOR = "#5f5383";

const sectionLabel = {
  fontSize: "12px", fontWeight: 600, color: "var(--sl-text-tertiary)",
  textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "10px",
};
const swatch = (color) => ({
  display: "inline-block", width: "8px", height: "8px", background: color,
  borderRadius: "2px", marginRight: "4px", verticalAlign: "middle",
});
const sectionWrap = { marginTop: "16px", paddingTop: "16px", borderTop: "1px solid var(--sl-border-secondary)" };

const SalesBreakdownCard = ({ ticketBreakdown, addOnBreakdown }) => {
  const presaleSold = ticketBreakdown.filter((t) => t.presale).reduce((s, t) => s + t.sold, 0);
  const publicSold = ticketBreakdown.filter((t) => !t.presale).reduce((s, t) => s + t.sold, 0);
  const splitTotal = presaleSold + publicSold;
  const hasPresale = ticketBreakdown.some((t) => t.presale);
  const presalePct = splitTotal > 0 ? Math.round((presaleSold / splitTotal) * 100) : 0;
  const publicPct = 100 - presalePct;

  return (
    <div className="d2-card">
      <div className="d2-card-header">
        <span className="d2-card-title">Sales Breakdown</span>
        <a className="d2-card-link" href="#tickets">All Ticket Types <i className="fas fa-arrow-right" style={{ fontSize: "10px" }} /></a>
      </div>

      {ticketBreakdown.length === 0 ? (
        <div className="card-detail-label">No ticket types yet.</div>
      ) : (
        ticketBreakdown.map((t, idx) => (
          <div className="d2-breakdown-row" key={t.id}>
            {/* ponytail: data-viz */}
            <div className="d2-breakdown-bar" style={{ background: BAR_COLORS[idx % BAR_COLORS.length] }} />
            <div className="d2-breakdown-info">
              <div className="d2-breakdown-name">{t.name}</div>
              <div className="d2-breakdown-sub">
                {t.sold.toLocaleString()} of {t.available.toLocaleString()} &middot; {t.sellThroughPct}%
              </div>
            </div>
            <div className="d2-breakdown-stats">
              <div>
                <div className="d2-breakdown-stat-label">Revenue</div>
                <div className="d2-breakdown-stat-value">{fmtCurrency(t.revenue)}</div>
              </div>
              <div>
                <div className="d2-breakdown-stat-label">Price</div>
                <div className="d2-breakdown-stat-value">{fmtCurrency(t.price)}</div>
              </div>
            </div>
          </div>
        ))
      )}

      {hasPresale && splitTotal > 0 && (
        <div style={sectionWrap}>
          <div style={sectionLabel}>Pre-Sale vs Public</div>
          <div style={{ display: "flex", height: "10px", borderRadius: "5px", overflow: "hidden" }}>
            <div style={{ width: `${presalePct}%`, background: PRESALE_COLOR }} title={`Pre-Sale: ${presaleSold} (${presalePct}%)`} />
            <div style={{ width: `${publicPct}%`, background: PUBLIC_COLOR }} title={`Public: ${publicSold} (${publicPct}%)`} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", marginTop: "6px" }}>
            <span style={{ color: PRESALE_COLOR, fontWeight: 600 }}>
              <span style={swatch(PRESALE_COLOR)} />Pre-Sale {presalePct}%
            </span>
            <span style={{ color: PUBLIC_COLOR, fontWeight: 600 }}>
              <span style={swatch(PUBLIC_COLOR)} />Public {publicPct}%
            </span>
          </div>
        </div>
      )}

      {addOnBreakdown.length > 0 && (
        <div style={sectionWrap}>
          <div style={sectionLabel}>Add-Ons</div>
          {addOnBreakdown.map((a) => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", padding: "6px 0" }}>
              <span>{a.name}</span>
              <span><strong>{a.sold}</strong>/{a.available} &middot; {fmtCurrency(a.revenue)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SalesBreakdownCard.propTypes = {
  ticketBreakdown: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    sold: PropTypes.number,
    available: PropTypes.number,
    sellThroughPct: PropTypes.number,
    price: PropTypes.number,
    revenue: PropTypes.number,
    presale: PropTypes.bool,
  })),
  addOnBreakdown: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    sold: PropTypes.number,
    available: PropTypes.number,
    revenue: PropTypes.number,
  })),
};

SalesBreakdownCard.defaultProps = {
  ticketBreakdown: [],
  addOnBreakdown: [],
};

export default SalesBreakdownCard;
